var util = require('lib/util');
var expand = util.expand;
var xmlToJson = require('xml2json');

var m_instanceConfigurationFlags = 0;
var m_usedModules = {
    addons: {}
};
var m_loaded = false;

/**
 * The namespace represents the main application interface for Cortona3D Solo.
 * 
 * @namespace 
 * @memberof module:solo
 * @property {number} DISABLE_DOCUMENT_INTERACTIVITY=0x000001 Disables the selection and highlighting of parts in the 3D window for catalog.
 * @property {number} DISABLE_PROCEDURE_TEXT_EMISSION=0x000002 Disables parsing and delivery text comments when loading procedure.
 * @property {number} DISABLE_AUDIO=0x000004 Disables audio in the procedure.
 * @property {number} DISABLE_VERTEX_ARRAY_OBJECT_OES=0x000008 Disables the OES_vertex_array_object extension (part of the WebGL API)  
 * @property {number} DISABLE_DISCARDABLE_GEOMETRY_DATA=0x000010 The flag must be set to enable geometry pick.<br>See {@link module:solo.app.pickObjectChain app.pickObjectChain()}.<br>See {@link NavigatorOptionsFlags NavigatorOptionsFlags}
 * @property {number} DISABLE_PROPERTY_VALUE_ANIMATIONS=0x000020 The flag must be set to turn off the animated color transition when objects are selected in the interactive catalog.
 * @property {number} ENABLE_GLES3=0x000040 The flag must be set to enable WebGL 2.0.
 * @property {number} DISABLE_ANTIALIASING_GLES3=0x000080 The flag must be set to disable MSAA anti-aliasing in the WebGL 2.0.
 * @property {number} TRANSPARENT_BACKGROUND=0x000100 The flag must be set to enable transparent background in the scene without VRML Background node.
 * @property {number} DISABLE_OBJECT_HOVERING=0x000200 Disables highlighting of parts in the 3D window for catalog.
 * @property {number} DISABLE_GEOMETRY_PREWARMING=0x000400 Prohibits the forced creation of all render objects.
 * @property {number} DEFAULT_WHELL_ACTION_ZOOM_MAC=0x000800 Enables zoom by default (without modifiers) for Mac.
 * @property {number} PHONG_LIGHTING=0x001000 Enables Phong lighting.
 * @property {number} ENABLE_NAVIGATION_FIT_TO_OBJECT=0x008000 Enables fit to object and change center of rotation in the 3D window.<br><b>Deprecated.</b> See {@link module:solo.app.navigator app.navigator}.
 * @property {number} DISABLE_INTERACTIVITY_XML_EMISSION=0x010000 Disables access to the interactivity.xml as JSON object.
 * @property {number} DRAWING_HOTSPOT_HIGHLIGHT_SOLID=0x020000 Determines whether to use the frame selection representation for selected hotspot area on the SVG image (default value), or to fill with another color.
 * @property {number} ENABLE_AUTO_SWITCHING_DISPLAY_MODE=0x040000 Enables automatic switching of display modes (2D and 3D) in the catalog.
 * @property {number} ENABLE_VRML_TEXT_NODE=0x080000 Enables VRML Text node support.
 * @property {number} DISABLE_DRAWING_INTERACTIVITY_OPTIMIZATION=0x100000 Disables optimization for the large drawing (more than 5000 text nodes).
 */
var app = {
    /**
     * Configures the Cortona3D Solo instance.
     * See {@link module:solo.app app} properties.
     * 
     * @param {number} setFlags - Flags bitmask 
     * @param {number} resetFlags - Flags bitmask
     */
    configureInstance: function (setFlags, resetFlags) {
        var set = setFlags || 0,
            reset = resetFlags || 0,
            TIRAMISU_FLAGS_MASK = 0x7FFF;
        m_instanceConfigurationFlags |= set;
        m_instanceConfigurationFlags &= ~reset;
        if (m_usedModules.core) {
            if (!Module.ccall || (Module.runtimeReady && !app.modelInfo)) {
                var Cortona3DSolo = require('solo');
                Cortona3DSolo.once('app.didFinishLoadDocument', function () {
                    app.configureInstance(set, reset);
                });
            } else {
                Module._tiramisu_configure(set & TIRAMISU_FLAGS_MASK, reset & TIRAMISU_FLAGS_MASK);
            }
        }
        if (m_usedModules.drawing) {
            var drawingFeatures = m_usedModules.drawing.config.features || 0;
            drawingFeatures |= set;
            drawingFeatures &= ~reset;
            m_usedModules.drawing.config.features = drawingFeatures;
            require('app.drawing').configure(m_usedModules.drawing.config);
        }
        return m_instanceConfigurationFlags;
    },

    /**
     * Initializes the Cortona3D Solo instance.
     * 
     * @memberof module:solo.app
     * @async
     * @function initialize
     * @param {string} [url] - The optional argument specifies the URL of the external file:
     * - companion file *.interactivity.xml*
     * - VRML file *.wrl*
     * - compressed (zip) package file containing the RapidAuthor publication *.cortona3d* *.vmb* *.zip*
     * - SVG file *.svg* 
     * - image file *.png* *.jpg* *.gif*
     * 
     * If omitted, the value specified in the `src` property is used.
     * @returns {Promise<DocumentInfo>} `Promise` whose fulfillment handler receives an {@link DocumentInfo} object when the requested file has successfully been loaded.
     * @see external:Promise
     * @see {@link module:solo.use use()}
     */
    initialize: function (canvas, url) {
        var src = url;
        var Cortona3DSolo = require('solo');

        if (typeof canvas === "string") {
            // initialize(src)
            src = canvas;

            if (!m_usedModules.drawing && !m_usedModules.core) {
                Cortona3DSolo.use('core', {
                    src: src,
                    features: m_instanceConfigurationFlags
                });
            }
        } else if (canvas && url) {
            // initialize(canvas, src)

            if (m_usedModules.core) {
                if (canvas) Cortona3DSolo.core.canvas = canvas;
            }

            Cortona3DSolo.use('core', {
                canvas: canvas,
                src: url,
                features: m_instanceConfigurationFlags
            });

            /*
            if (!app.drawing) {
                var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                canvas.parentNode.insertBefore(svg, canvas);
                Cortona3DSolo.use('drawing', {
                    svg: svg,
                    features: m_instanceConfigurationFlags
                });
            }
            */
        }

        function loaded() {
            m_loaded = true;
        }

        Cortona3DSolo.once('app.didFinishLoadDocument', loaded);
        Cortona3DSolo.once('app.didFailLoadDocument', loaded);

        if (m_usedModules.drawing) {
            require('app.drawing').attach(Cortona3DSolo.core && Cortona3DSolo.core.canvas);
            if (!m_usedModules.core) {
                src = src || m_usedModules.drawing.config.src;
                if (src) {
                    Cortona3DSolo.app.drawing.show(true);
                    return new Promise(function (resolve, reject) {
                        Cortona3DSolo.once('app.didFinishLoadDocument', resolve);
                        Cortona3DSolo.app.drawing.load(src).catch(reject);
                    });
                }
                return Promise.resolve({});
            }
        }

        if (m_usedModules.core) {
            if (Cortona3DSolo.core.canvas) {
                Cortona3DSolo.core.canvas.setAttribute('touch-action', 'none');
                Cortona3DSolo.core.canvas.style.touchAction = 'none';
            }

            var moduleArguments = require('core.solo').Arguments;

            if (m_usedModules.tag) {
                moduleArguments.instanceClass = 'stella';
            } else {
                src = src || moduleArguments.document;

                if (src) {
                    moduleArguments.document = Cortona3DSolo.app.util.toUrl(src);
                } else {
                    moduleArguments.document = URL.createObjectURL(new Blob(["#VRML V2.0 utf8"], {
                        type: "model/vrml"
                    }));
                }
            }

            moduleArguments.features = (m_instanceConfigurationFlags & 32767);
            moduleArguments.contentScaleFactor = window.devicePixelRatio || 1;
            moduleArguments.toModule();

            if (m_usedModules.tag) {
                return new Promise(function (resolve, reject) {
                    Cortona3DSolo.once('app.firstFrameDidArrive', resolve);
                });
            }

            return new Promise(function (resolve, reject) {
                Cortona3DSolo.once("core.onError", reject);
                Cortona3DSolo.once('app.didFinishLoadDocument', resolve);
                Cortona3DSolo.once('app.didFailLoadDocument', reject);
            });
        }

        return Promise.reject(new Error('Solo initialize: nothing to do'));
    },

    version: function () {
        return VERSION;
    },

    /**
     * Returns true if the library has already been initialized and the document has already been loaded.
     * 
     * @memberof module:solo.app
     * @function isDocumentLoaded
     * @returns {boolean} 
     * @since 1.4.3
     */
    isDocumentLoaded: function () {
        return m_loaded;
    },

    /**
     * _Asynchronous method_. Loads the companion file.
     * 
     * @async
     * @memberof module:solo.app
     * @function loadCompanionFile
     * @param {string} url - An URL to load the external companion file *.interactivity.xml*
     * @returns {Promise<CompanionFileInfo>} `Promise` whose fulfillment handler receives an {@link CompanionFileInfo} object when the requested file has successfully been loaded.
     * @fires "app.onProgress"
     * @see external:Promise
     * @since 1.4.0
     */
    loadCompanionFile: function (url) {
        return util.loadResource(url, 'text/xml')
            .then(function (xhr) {
                var data,
                    json,
                    type = '',
                    meta = [];

                if (!xhr.responseXML) {
                    throw new Error(url + " empty content");
                }

                json = xmlToJson(xhr.responseXML);

                data = {
                    baseURL: util.getBaseURL(url),
                    options: require('interactivity.options.js')(json),
                    interactivity: {
                        document: xhr.responseXML,
                        json: json
                    }
                };

                data.specificationName = data.options.SpecID;

                if (json.ipc) {
                    expand(data,
                        {
                            type: 'ipc',
                            metadata: require('interactivity.metadata.js')(json.$('ipc/figure/metadata/value'))
                        },
                        require('app.ipc.interactivity.js')(data.interactivity, data.baseURL)
                    );
                } else if (json.SimulationInteractivity) {
                    expand(data,
                        {
                            type: 'procedure',
                            metadata: require('interactivity.metadata.js')(json.$('SimulationInteractivity/SimulationInformation/metadata/value'))
                        },
                        require('app.procedure.interactivity.js')(data.interactivity, data.baseURL)
                    );
                }

                return data;
            });
    },
    /**
     * Returns the absolute URL or data URL of the resource file for the loaded interactive model.
     * 
     * @memberof module:solo.app
     * @param {string} url
     * @param {string} [baseUrl]
     * @returns {string}
     * @since 1.7.0
     */
    getResourceUrl: function (url, baseUrl) {
        var res;
        if (baseUrl && !/^(data|blob)/i.test(baseUrl)) {
            // using baseUrl
            res = app.util.toUrl(url, baseUrl);
        } else if (app.modelInfo && app.modelInfo.bundleURL) {
            // using bundle
            res = app.util.createResourceURL(url) || app.util.toUrl(url, app.modelInfo.bundleURL);
        } else {
            // using interactivity
            res = app.util.toUrl(url, app.modelInfo && app.modelInfo.baseURL);
        }
        return res;
    },
    navigator: {
        inertiaDecelerationFactor: 1,
        inertiaDisabled: false
    }
};

Object.defineProperty(app, 'DISABLE_DOCUMENT_INTERACTIVITY', {
    value: 1,
    writable: false
});
Object.defineProperty(app, 'DISABLE_PROCEDURE_TEXT_EMISSION', {
    value: 2,
    writable: false
});
Object.defineProperty(app, 'DISABLE_AUDIO', {
    value: 4,
    writable: false
});
Object.defineProperty(app, 'DISABLE_VERTEX_ARRAY_OBJECT_OES', {
    value: 8,
    writable: false
});
Object.defineProperty(app, 'DISABLE_DISCARDABLE_GEOMETRY_DATA', {
    value: 16,
    writable: false
});
Object.defineProperty(app, 'DISABLE_PROPERTY_VALUE_ANIMATIONS', {
    value: 32,
    writable: false
});
Object.defineProperty(app, 'ENABLE_GLES3', {
    value: 64,
    writable: false
});
Object.defineProperty(app, 'DISABLE_ANTIALIASING_GLES3', {
    value: 128,
    writable: false
});
Object.defineProperty(app, 'TRANSPARENT_BACKGROUND', {
    value: 256,
    writable: false
});
Object.defineProperty(app, 'DISABLE_OBJECT_HOVERING', {
    value: 512,
    writable: false
});
Object.defineProperty(app, 'DISABLE_GEOMETRY_PREWARMING', {
    value: 1024,
    writable: false
});
Object.defineProperty(app, 'DEFAULT_WHELL_ACTION_ZOOM_MAC', {
    value: 2048,
    writable: false
});
Object.defineProperty(app, 'PHONG_LIGHTING', {
    value: 4096,
    writable: false
});
Object.defineProperty(app, 'ENABLE_NAVIGATION_FIT_TO_OBJECT', {
    value: 32768,
    writable: false
});
Object.defineProperty(app, 'DISABLE_INTERACTIVITY_XML_EMISSION', {
    value: 32768 * 2,
    writable: false
});
Object.defineProperty(app, 'DRAWING_HOTSPOT_HIGHLIGHT_SOLID', {
    value: 32768 * 4,
    writable: false
});
Object.defineProperty(app, 'ENABLE_AUTO_SWITCHING_DISPLAY_MODE', {
    value: 32768 * 8,
    writable: false
});
Object.defineProperty(app, 'ENABLE_VRML_TEXT_NODE', {
    value: 32768 * 16,
    writable: false
});
Object.defineProperty(app, 'DISABLE_DRAWING_INTERACTIVITY_OPTIMIZATION', {
    value: 32768 * 32,
    writable: false
});


module.exports = {
    app: app,
    checkConfigureOptions: function (options) {
        return (m_instanceConfigurationFlags & options) === options;
    },
    getInstanceConfigurationFlags: function () {
        return m_instanceConfigurationFlags;
    },
    getDocumentURL: function () {
        var res = "";
        if (m_usedModules.core) {
            var args = Module.arguments;
            if (args) {
                var i = args.indexOf('document');
                if (i >= 0) {
                    res = args[i + 1];
                }
            }
        }
        return res;
    },
    usedModules: m_usedModules
};

/**
 * Information from the loaded companion file, for example:
 * ```
 * {
 *      type: 'ipc',
 *      metadata: {
 *          TITLE: "Pump IPC - Complete",
 *          ...
 *      },
 *      options: {
 *          SpecID: "GENERIC_IPC",
 *          SpecLang: "en",
 *          ...
 *      },
 *      specificationName: "GENERIC_IPC",
 *      interactivity: { ... }
 * }
 * ```
 * @prop {string} type - Name of the companion file type:
 * - `"ipc"`
 * - `"procedure"`
 * @prop {KeyValuePairs} metadata - An object represents project metadata from the companion file.
 * @prop {KeyValuePairs} options - An object represents published options from the companion file.
 * @prop {string} specificationName - Identifier of the specification component.
 * @prop {object} interactivity - The namespace corresponding to the type of companion file. See {@link module:solo.app.ipc.interactivity app.ipc.interactivity} and {@link module:solo.app.procedure.interactivity app.procedure.interactivity}.
 * @prop {string} baseURL - A base path to the loaded companion file or the empty string if the model loaded from the bundle file.
 * @typedef {object} CompanionFileInfo
 * @see {@link module:solo.app.loadCompanionFile app.loadCompanionFile()}
 */